import { useCallback, useEffect, useState } from 'react';
import { CategoryPageHeader } from './CategoryPageHeader';
import { SearchInput } from '../publications/form/SearchInput';
import { useSearch } from '../hooks/useSearch';
import Spinner from '../spinner/Spinner';
import { Category, CategoryStatus, getCategories, updateCategory } from '../api/category';
import { ArchiveCategoryCard } from './ArchiveCategoryCard';
import { CategoryUnarchiveModal } from './Modals/CategoryUnarchiveModal';
import { EmptyCategory } from './EmptyCategory';
import { useCategoryPublications } from './hooks/useCategoryPublications';
import { PageLayout } from '../layout/PageLayout';
import { ButtonLink } from '../components/Button';
import { Option } from '../types/option';
import { getCategoryFlowErrorMessage } from '../api/problemJson';

const UNARCHIVE_API_ERROR_MESSAGE = 'Un problème est survenu.';

export function ArchiveCategories() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [isUnarchiveModalOpened, setIsUnarchiveModalOpened] = useState<boolean>(false);
    const [currentUnarchiveCategory, setCurrentUnarchiveCategory] = useState<Option<Category>>(
        Option.none(),
    );
    const [unarchiveModalApiError, setUnarchiveModalApiError] = useState<Option<string>>(
        Option.none(),
    );

    const {
        getPublications,
        loadPage,
        refreshCategory,
        isCategoryLoading,
        getPublicationsLoadError,
    } = useCategoryPublications();

    const handleCategories = useCallback(async (search: Option<string>) => {
        setIsLoading(true);
        const data = await getCategories(search, Option.some(CategoryStatus.ARCHIVE));
        setCategories(data);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        void handleCategories(Option.none());
    }, [handleCategories]);

    const reloadCategories = useCallback(() => {
        void handleCategories(Option.none());
    }, [handleCategories]);

    const searchCategories = useCallback(
        (query: string) => handleCategories(Option.some(query)),
        [handleCategories],
    );

    const [search, isSearching, handleSearchText] = useSearch(
        searchCategories,
        3,
        300,
        reloadCategories,
    );

    const handleCloseUnarchiveModal = () => {
        setUnarchiveModalApiError(Option.none());
        setCurrentUnarchiveCategory(Option.none());
        setIsUnarchiveModalOpened(false);
    };

    const handleUnarchive = async () => {
        await currentUnarchiveCategory
            .map(async category => {
                setUnarchiveModalApiError(Option.none());

                try {
                    await updateCategory(category.id, {
                        title: category.title,
                        status: CategoryStatus.ACTIVE,
                    });

                    await handleCategories(Option.none());
                    handleCloseUnarchiveModal();
                } catch (error: unknown) {
                    setUnarchiveModalApiError(
                        Option.some(
                            getCategoryFlowErrorMessage(error, UNARCHIVE_API_ERROR_MESSAGE),
                        ),
                    );
                }
            })
            .getOrElse(Promise.resolve());
    };

    const openUnarchive = (c: Category) => {
        setUnarchiveModalApiError(Option.none());
        setCurrentUnarchiveCategory(Option.some(c));
        setIsUnarchiveModalOpened(true);
    };

    const listLoading = isLoading || isSearching;

    return (
        <>
            <PageLayout>
                <CategoryPageHeader
                    actions={
                        <ButtonLink to="/categories" isPrimary={false}>
                            Mes catégories
                        </ButtonLink>
                    }
                />
                <div className="my-6 w-full">
                    <SearchInput
                        search={search}
                        handleSearch={handleSearchText}
                        placeholder="Rechercher une categorie..."
                    />
                </div>
                <Spinner loading={listLoading} />
                {!listLoading && categories.length === 0 && (
                    <EmptyCategory title="Aucune catégorie archivée" />
                )}
                {!listLoading && categories.length > 0 && (
                    <ul className="flex flex-col gap-4">
                        {categories.map(category => (
                            <ArchiveCategoryCard
                                key={category.id}
                                category={category}
                                publications={Option.some(getPublications(category.id))}
                                loadingCategoryPublication={isCategoryLoading(category.id)}
                                publicationsLoadError={getPublicationsLoadError(category.id)}
                                handleUnarchive={openUnarchive}
                                onLoadPublications={loadPage}
                                onRefreshPublications={refreshCategory}
                            />
                        ))}
                    </ul>
                )}
            </PageLayout>
            {currentUnarchiveCategory
                .map(category => (
                    <CategoryUnarchiveModal
                        isOpen={isUnarchiveModalOpened}
                        category={category}
                        handleClose={handleCloseUnarchiveModal}
                        handleConfirm={handleUnarchive}
                        apiError={unarchiveModalApiError}
                    />
                ))
                .toNull()}
        </>
    );
}
